import { HttpClient, httpResource } from '@angular/common/http';
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { AuthStore } from '../../core/auth/auth.store';
import { DeletedSite } from './collaboration.models';
import {
  CreateSiteRequest,
  CreationCheck,
  GeoPlace,
  SiteDetail,
  SiteOverview,
  SiteStatus,
  SitesResponse,
  UpdateSiteRequest,
} from './sites.models';

/**
 * Accesso ai cantieri (contracts/api-sites.md). La Dashboard e la barra laterale leggono la
 * stessa risorsa `all`; creazione, modifica e immagine passano da `HttpClient`.
 */
@Injectable({ providedIn: 'root' })
export class SitesApi {
  private readonly auth = inject(AuthStore);
  private readonly http = inject(HttpClient);

  /**
   * Tutti i cantieri accessibili, senza filtro: alimenta la barra laterale, gli account visibili
   * e la Dashboard senza filtro. Si ricarica quando cambia l'utente.
   */
  readonly all = httpResource<SitesResponse>(() => (this.auth.user() ? '/api/sites' : undefined));

  /** I cantieri di un account, per il filtro della Dashboard. */
  sitesOf(accountId: Signal<string | null>) {
    return httpResource<SitesResponse>(() => {
      const id = accountId();
      return id && this.auth.user() ? { url: '/api/sites', params: { accountId: id } } : undefined;
    });
  }

  /** Se l'account può creare un altro cantiere attivo (FR-034). */
  creationCheck(accountId?: string | null): Observable<CreationCheck> {
    return this.http.get<CreationCheck>('/api/sites/creation-check', {
      params: accountId ? { accountId } : {},
    });
  }

  detail(siteId: string): Observable<SiteDetail> {
    return this.http.get<SiteDetail>(`/api/sites/${encodeURIComponent(siteId)}`);
  }

  create(request: CreateSiteRequest): Observable<SiteDetail> {
    return this.http.post<SiteDetail>('/api/sites', request);
  }

  update(siteId: string, request: UpdateSiteRequest): Observable<SiteDetail> {
    return this.http.put<SiteDetail>(`/api/sites/${encodeURIComponent(siteId)}`, request);
  }

  /** I propri ruoli professionali nel cantiere (FR-006, FR-014b). */
  updateMyRoles(
    siteId: string,
    professionalRoles: readonly string[],
    acknowledgedWarnings: readonly string[],
  ): Observable<SiteDetail> {
    return this.http.put<SiteDetail>(`/api/sites/${encodeURIComponent(siteId)}/me/roles`, {
      professionalRoles,
      acknowledgedWarnings,
    });
  }

  /** Carica l'immagine 16:9 già ritagliata (FR-009); il server la ricodifica in WEBP. */
  uploadImage(siteId: string, file: Blob): Observable<{ readonly imageVersion: string }> {
    const body = new FormData();
    body.append('file', file, 'site.jpg');
    return this.http.put<{ imageVersion: string }>(
      `/api/sites/${encodeURIComponent(siteId)}/image`,
      body,
    );
  }

  deleteImage(siteId: string): Observable<void> {
    return this.http
      .delete(`/api/sites/${encodeURIComponent(siteId)}/image`)
      .pipe(map(() => undefined));
  }

  archive(siteId: string): Observable<SiteDetail> {
    return this.http.post<SiteDetail>(`/api/sites/${encodeURIComponent(siteId)}/archive`, null);
  }

  /** Riattiva nel limite del piano (409 `site_limit_reached`). */
  reactivate(siteId: string): Observable<SiteDetail> {
    return this.http.post<SiteDetail>(`/api/sites/${encodeURIComponent(siteId)}/reactivate`, null);
  }

  /** Elimina con la conferma del nome; il 403 `reauthentication_required` apre la conferma dell'identità. */
  delete(siteId: string, confirmName: string): Observable<{ readonly purgeAfter: string }> {
    return this.http.post<{ purgeAfter: string }>(
      `/api/sites/${encodeURIComponent(siteId)}/delete`,
      { confirmName },
    );
  }

  restore(siteId: string): Observable<{ readonly id: string; readonly status: SiteStatus }> {
    return this.http.post<{ id: string; status: SiteStatus }>(
      `/api/sites/${encodeURIComponent(siteId)}/restore`,
      null,
    );
  }

  deleted(): Observable<readonly DeletedSite[]> {
    return this.http.get<DeletedSite[]>('/api/sites/deleted');
  }

  /** Suggerimenti di indirizzo; se il servizio non risponde l'elenco è vuoto (indirizzo a mano). */
  searchPlaces(query: string): Observable<readonly GeoPlace[]> {
    return this.http
      .get<GeoPlace[]>('/api/geocoding/search', { params: { q: query } })
      .pipe(catchError(() => of([])));
  }

  /** L'indirizzo del punto scelto sulla mappa, o `null` se non si trova. */
  reversePlace(latitude: number, longitude: number): Observable<GeoPlace | null> {
    return this.http
      .get<GeoPlace>('/api/geocoding/reverse', { params: { lat: latitude, lng: longitude } })
      .pipe(catchError(() => of(null)));
  }
}

/**
 * La Panoramica del cantiere selezionato, condivisa fra la pagina, il percorso della testata e
 * il titolo della finestra, così da chiederla una volta sola.
 */
@Injectable({ providedIn: 'root' })
export class SiteOverviewStore {
  private readonly selected = signal<string | null>(null);

  readonly siteId = this.selected.asReadonly();
  readonly overview = httpResource<SiteOverview>(() => {
    const id = this.selected();
    return id ? `/api/sites/${encodeURIComponent(id)}/overview` : undefined;
  });
  readonly siteName = computed(() =>
    this.overview.hasValue() ? this.overview.value().site.name : null,
  );
  readonly account = computed(() =>
    this.overview.hasValue() ? this.overview.value().account : null,
  );

  select(siteId: string | null): void {
    this.selected.set(siteId);
  }
}
